import { Link } from 'react-router-dom';
import {
    PiArrowDownDuotone,
    PiArrowRightDuotone,
    PiQuotesDuotone,
} from 'react-icons/pi';

import Noise from 'elements/Noise';
import Pattern from 'elements/Pattern';
import { mekiJobAreas } from 'data/mekiPages';

export function MekiHero({ title, lead, image, contentId, showApply = false }) {
    const scrollToContent = () => {
        document.getElementById(contentId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <section className='relative z-20 min-h-[calc(100svh-5rem)] overflow-hidden rounded-b-3xl shadow-2xl isolate'>
            <img src={image} alt='' className='absolute inset-0 size-full object-cover object-center' />
            <div className='absolute inset-0 bg-gradient-to-t from-red-950 via-red-950/65 to-red-950/15 md:bg-gradient-to-r md:from-red-950/90 md:via-red-950/50 md:to-red-950/15' />
            <Noise />

            <div className='relative z-10 mx-auto flex min-h-[calc(100svh-5rem)] w-full max-w-boxed items-center px-site py-20'>
                <div className='flex max-w-5xl flex-col items-center text-center md:items-start md:text-left'>
                    <h1 className='font-display text-4xl font-black leading-[0.98] text-yellow-300 text-balance sm:text-6xl lg:text-7xl xl:text-8xl'>
                        {title}
                    </h1>
                    {lead && <p className='mt-6 max-w-3xl text-base leading-relaxed text-white/90 sm:text-lg lg:text-xl'>{lead}</p>}

                    <div className='mt-8 flex w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row'>
                        {showApply && (
                            <Link to='/diakmunka' className='button gradient-green shadow-xl shadow-red-950/20'>
                                Jelentkezem <PiArrowRightDuotone />
                            </Link>
                        )}
                        <button type='button' onClick={scrollToContent} className='button bg-red-950/45 backdrop-blur-md hover:bg-red-950/65'>
                            Tovább <PiArrowDownDuotone />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}

export function MekiSectionHeading({ children, light = false, centered = false }) {
    return (
        <h2 className={`font-display text-3xl font-black leading-tight text-balance sm:text-4xl lg:text-5xl xl:text-6xl ${light ? 'text-yellow-300' : 'text-red-700'} ${centered ? 'text-center' : ''}`}>
            {children}
        </h2>
    );
}

export function Paragraphs({ items, className = '' }) {
    return (
        <div className={`flex flex-col gap-4 text-sm leading-relaxed text-neutral-700 sm:text-base lg:text-lg ${className}`}>
            {items.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
        </div>
    );
}

export function MekiQuote({ paragraphs, name }) {
    return (
        <blockquote className='relative overflow-hidden rounded-3xl bg-gradient-to-br from-red-800 via-red-700 to-red-600 p-7 text-white shadow-2xl sm:p-10 lg:p-12'>
            <Pattern className='pointer-events-none opacity-50' />
            <PiQuotesDuotone className='relative z-10 mb-5 size-12 text-yellow-300 sm:size-14' />
            <div className='relative z-10 flex flex-col gap-4 text-sm italic leading-relaxed text-white/90 sm:text-base'>
                {paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
            </div>
            {name && <footer className='relative z-10 mt-6 text-right font-display text-base font-black text-yellow-300 sm:text-lg'>{name}</footer>}
        </blockquote>
    );
}

export function ApplyButton({ className = '' }) {
    return (
        <Link to='/diakmunka' className={`button gradient-green shadow-xl ${className}`}>
            Jelentkezem <PiArrowRightDuotone />
        </Link>
    );
}

export function MekiOffersCta() {
    return (
        <section className='relative overflow-hidden rounded-3xl bg-gradient-to-br from-yellow-400 via-amber-400 to-lime-500 px-site py-14 shadow-2xl sm:py-20'>
            <Noise />
            <div className='relative z-10 mx-auto flex w-full max-w-boxed flex-col items-center text-center'>
                <MekiSectionHeading centered>Diákmunka ajánlataink</MekiSectionHeading>
                <Link to='/diakmunka' className='button gradient-red mt-8 shadow-xl'>
                    További ajánlatok keresése <PiArrowRightDuotone />
                </Link>
            </div>
        </section>
    );
}

export function MekiWorkOverview({ excludeKey }) {
    return (
        <>
            <section className='relative bg-stone-50 px-site py-16 sm:py-24'>
                <div className='mx-auto flex w-full max-w-boxed flex-col gap-8 sm:gap-10'>
                    <MekiSectionHeading>A Mekis munkáról</MekiSectionHeading>
                    <div className='rounded-3xl bg-white p-6 shadow-xl shadow-red-900/5 sm:p-10 lg:p-12'>
                        <Paragraphs items={[
                            'A Mekiben a csapatmunka az első, és tudnod kell, hogy soha nem vagy egyedül. Mind a Meki mentor programja, mind a Fürge Diák Mekis csapata ott áll mellett, ha kérdésed adódik vagy segítségre van szükséged. Az étteremben folyamatos támogatással tanulod meg az egyes munkafolyamatokat, és rövid idő alatt magabiztosan mozogsz majd az egész étteremben.',
                            'A Mekis diákmunka pörgős. Nincs idő lustálkodásra, de szükség van az önállóságra. A csapat csak úgy tudja kiszolgálni a vendégeket, ha mindenki felelősséggel végzi el a rá bízott feladatokat. És bár diákmunkát vállalsz, ugyanolyan Mekis leszel, mint bárki más. A Meki nem tesz különbséget diák és nem diák között. Felnőttként tekintenek rád, és az elvárások is eszerint alakulnak.',
                        ]} />
                        <p className='mt-6 font-display text-xl font-black text-red-700 sm:text-2xl'>
                            Érdekel a kihívás? Ismerd meg diákmunka ajánlatainkat!
                        </p>
                    </div>
                </div>
            </section>

            <section className='relative overflow-hidden bg-black/40 px-site py-16 sm:py-24'>
                <Pattern />
                <div className='relative z-10 mx-auto grid w-full max-w-boxed grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4'>
                    {mekiJobAreas.map((item) => <MekiRoleCard key={item.key} item={item} active={item.key === excludeKey} />)}
                </div>
            </section>
        </>
    );
}

function MekiRoleCard({ item, active }) {
    return (
        <Link
            to={`/munkakor?key=${item.key}`}
            aria-current={active ? 'page' : undefined}
            className='group overflow-hidden rounded-3xl bg-white shadow-2xl shadow-red-950/15 transition duration-500 hover:-translate-y-2'
        >
            <div className='relative aspect-[4/3] overflow-hidden'>
                <img src={item.image} alt='' className='size-full object-cover transition duration-700 group-hover:scale-105' />
                <div className='absolute inset-x-0 bottom-0 bg-gradient-to-t from-red-950/80 to-transparent p-5 pt-14'>
                    <span className='flex items-center justify-between gap-3 text-sm font-bold text-white'>
                        Kattints a részletekért! <PiArrowRightDuotone className='size-5 shrink-0' />
                    </span>
                </div>
            </div>
            <div className='p-6'>
                <h3 className='font-display text-2xl font-black leading-tight text-red-700'>{item.title}</h3>
                <p className='mt-3 text-sm leading-relaxed text-neutral-600'>{item.text}</p>
            </div>
        </Link>
    );
}
